<template>
  <div
    :id="uuid"
    ref="ticketsBlock"
    class="p-6 md:p-8 bg-white rounded-xl gap-4"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
      'background-color': formData.bgColor,
    }"
  >
    <div
      v-if="
        (formData.title || formData.subtitle) &&
        !(formData.hideOnEmbed && isInIframe)
      "
      class="mb-6"
    >
      <h2
        v-if="formData.title"
        class="text-2xl/[1] md:text-3xl/[1.20] font-bold mb-1"
        :style="{
          textAlign: formData.titleAlign,
          color: formData.titleColor,
          fontWeight: formData.titleWeight,
          fontStyle: formData.titleStyle,
          fontSize: formData.titleSizePx,
          ...useEditorFontFamily(formData.titleFontFamily),
        }"
      >
        {{ formData.title }}
      </h2>
      <div
        v-if="formData.subtitle"
        class="text-xl"
        :style="{
          textAlign: formData.subtitleAlign || undefined,
          color: formData.subtitleColor || undefined,
          fontWeight: formData.subtitleWeight || undefined,
          fontStyle: formData.subtitleStyle || undefined,
          fontSize: formData.subtitleSizePx || undefined,
          ...useEditorFontFamily(formData.subtitleFontFamily),
        }"
      >
        {{ formData.subtitle }}
      </div>
    </div>
    <div v-if="isTicketsLoading" class="p-24 flex justify-center">
      <BaseSpinner />
    </div>
    <div
      v-else-if="formData.categories"
      class="tickets-container w-full"
      :class="isInIframe && 'pb-2'"
    >
      <div
        class="tickets-inner w-full flex flex-wrap"
        :class="availableTicketsCategories?.length === 1 && 'oneItem'"
        :style="{ '--tickets-items-align-desktop': props.formData.itemsAlign }"
      >
        <template
          v-for="(category, index) in availableTicketsCategories"
          :key="category.title + index"
        >
          <TicketsCategoryCard
            class="ticket-card-width"
            :class="isInIframe && 'in-iframe'"
            :style="{
              '--tickets-columns-large-desktop': colsNumberOnLargeDesktop,
              '--tickets-columns-small-desktop': colsNumberOnSmallDesktop,
            }"
            :category="category"
            :tickets-info="ordersStore.ticketsDetails"
            :is-in-iframe="isInIframe"
            :free-ticket-registration-id="freeTicketRegistrationId"
            @update="onUpdate"
            @go-to-cart="onClickOnCart"
            @register-free-ticket="onRegisterFreeTicket"
          />
        </template>
      </div>
      <BaseObserve empty @change="onVisibleChange" />
    </div>
    <div class="w-full flex flex-col gap-4">
      <div
        v-if="formData.footerText"
        class="marked mt-6"
        :style="{
          color: formData.footerTextColor,
          ...useEditorFontFamily(formData.footerTextFontFamily),
        }"
        v-html="
          getMarkedTextWithBlankLinks(
            formData.footerText,
            formData.footerTextLinkColor
          )
        "
      />
      <div v-if="formData.footerButtons?.length" class="flex flex-wrap gap-4">
        <template
          v-for="(button, index) in formData.footerButtons"
          :key="index"
        >
          <EditorButton :button="button" />
        </template>
      </div>
    </div>
    <BaseLink
      v-if="
        isMounted && !isWaitExternalCart && !isExternalCart && ticketsCounter
      "
      :to="isInIframe ? undefined : ordersStore.cartRoute"
      :href="ordersStore.cartLink"
      target="_blank"
      class="!fixed z-10 right-4 rounded-xl flex items-center justify-center bg-white !p-4 shadow-md group transform duration-150"
      :class="[isCartExpanded && 'scale-125']"
      style="top: calc(5rem + var(--sat))"
      size="none"
      theme="none"
      @click="onClickOnCart"
    >
      <BaseIcon name="outline_ticket_thin" size="xl" class="text-primary-800" />
      <div
        class="absolute -top-2 -right-2 flex items-center justify-center bg-red-500 rounded-full h-5 w-5 text-white text-xs"
      >
        {{ ticketsCounter }}
      </div>
    </BaseLink>
    <div
      v-if="formData.showBanner"
      class="fixed bottom-0 right-0 z-40 transition-all duration-700 max-w-full p-2"
      :class="isBannerVisible ? 'translate-y-0' : 'translate-y-full opacity-0'"
      :style="{
        width: formData.bannerWidth + 'px',
      }"
    >
      <div
        class="bottom-full right-0 pr-2"
        :class="isBannerVisible ? 'absolute' : 'hidden'"
      >
        <button
          look="none"
          size="sm"
          class="rounded-full text-gray-600 bg-white/50 p-1.5 hover:bg-white/90 hover:text-gray-800 transition-all"
          @click="closeBanner"
        >
          <BaseIcon name="outline_x" />
        </button>
      </div>
      <div
        class="flex items-center justify-center overflow-hidden shadow-[0_0px_21px_-4px_rgba(10,10,10,0.3)] cursor-pointer hover:-translate-y-[4px] transition-all"
        :style="{
          color: formData.bannerTextColor,
          backgroundColor: formData.bannerBackgroundColor,
          fontSize: formData.bannerTextSize + 'px',
          fontWeight: formData.bannerTextWeight,
          borderColor: formData.bannerBorderColor,
          borderWidth: formData.bannerBorderWidth + 'px',
          paddingLeft: formData.bannerPaddingLeft + 'px',
          paddingRight: formData.bannerPaddingRight + 'px',
          paddingTop: formData.bannerPaddingVertical + 'px',
          paddingBottom: formData.bannerPaddingVertical + 'px',
          borderRadius: formData.bannerBorderRadius + 'px',
        }"
        @click="onBannerClick"
      >
        <img
          v-if="formData.bannerImgLeft"
          :src="formData.bannerImgLeft"
          class="min-w-0"
          :style="{ maxHeight: formData.bannerImgLeftHeight + 'px' }"
        />
        <div
          v-if="formData.bannerText"
          class="whitespace-pre-wrap break-words shrink"
          :style="{
            paddingLeft: formData.bannerTextPaddingLeft + 'px',
            paddingRight: formData.bannerTextPaddingRight + 'px',
          }"
        >
          {{ formData.bannerText }}
        </div>
        <img
          v-if="formData.bannerImgRight"
          :src="formData.bannerImgRight"
          class="min-w-0"
          :style="{ maxHeight: formData.bannerImgRightHeight + 'px' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash-es/debounce'
import { useI18n } from 'vue-i18n'
import type { Category } from './TicketsCategoryCard.vue'
import TicketsCategoryCard from './TicketsCategoryCard.vue'
import { useEvents } from '~/stores/events'
import { useOrders } from '~/stores/orders'
import { usePageEditor } from '~/stores/page-editor'
import type { TicketDetails } from '~/models/orders'
import { YandexECommerceEventType } from '~/models/ecommerce-yandex'
import EditorButton from '~/components/page-editor/EditorButton.vue'
import { useNotifications } from '~/stores/notifications'
import { useAuth } from '~/stores/auth'

const { getMarkedTextWithBlankLinks } = useMarkedText()
const authStore = useAuth()
const { t } = useI18n()
const { pushNotification } = useNotifications()

const eventSlug = useRoute().params.event as string

interface PropsInterface {
  uuid: string
  formData: any
  isInIframe?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isInIframe: false,
})

const SCROLL_TRIGGER = 200
const isScrolled = ref(false)
const isBannerClosed = ref(false)
const isTicketsVisible = ref(false)
const intersectionObserver = ref<null | IntersectionObserver>(null)
const freeTicketRegistrationId = ref<TicketDetails['id'] | null>(null)

const availableTicketsCategories = computed(() => {
  return props.formData.categories
    ?.map((category: Category) => {
      // If no tickets are left, remove the entire category, but keep categories that were originally empty (i.e., tickets array is [])
      const tickets = category?.tickets?.filter(
        (item) => ordersStore.ticketsDetailsMap[item.value]?.available
      )
      return {
        ...category,
        tickets:
          category?.tickets.length && tickets?.length === 0 ? null : tickets,
      }
    })
    ?.filter((category: Category) => category.tickets)
})

const isBannerVisible = computed(
  () =>
    props.formData.showBanner &&
    !props.isInIframe &&
    isScrolled.value &&
    !isBannerClosed.value &&
    !isTicketsVisible.value &&
    eventsStore?.event?.slug === eventSlug &&
    !eventsStore?.event?.is_has_ticket &&
    !ticketsCounter.value
)

function closeBanner() {
  isBannerClosed.value = true
}

const onScroll = debounce(() => {
  requestAnimationFrame(() => {
    if (window.scrollY > SCROLL_TRIGGER) {
      isScrolled.value = true
    } else {
      isScrolled.value = false
    }
  })
}, 150)
onMounted(() => {
  document.addEventListener('scroll', onScroll, { passive: true })

  intersectionObserver.value = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        isTicketsVisible.value = true
      } else {
        isTicketsVisible.value = false
      }
    })
  })

  ticketsBlock.value && intersectionObserver.value.observe(ticketsBlock.value)
})

onUnmounted(() => {
  document.removeEventListener('scroll', onScroll)
  intersectionObserver.value?.disconnect()
})

function onBannerClick() {
  ticketsBlock.value?.scrollIntoView({ behavior: 'smooth' })
}

const pageEditorStore = usePageEditor()

const emit = defineEmits(['loaded'])
const eventsStore = useEvents()
const ordersStore = useOrders()
const {
  getSessionStorageValue,
  setSessionStorageValue,
  removeSessionStorageValue,
} = useSessionStorage()
const {
  $yandexMetrika: {
    sendYandexECommerceEvent,
    getYandexECommerceProductFromTicket,
  },
  $linkedin: { lintrk },
  $facebook: { fbq },
  $gtm: {
    gTagPush,
    sendAddToCartGoogleECommerceEvent,
    getGoogleECommerceItemFromTicket,
    sendViewItemListGoogleECommerceEvent,
  },
} = useNuxtApp()

const { selectedTickets } = storeToRefs(ordersStore)
selectedTickets.value = {}
const isTicketsLoading = ref(false)
const isCartExpanded = ref(false)
const ticketsBlock = ref<HTMLElement | null>(null)

const ticketsCounter = computed(() => {
  return Object.values(selectedTickets.value).reduce(
    (acc, count) => acc + count,
    0
  )
})

const currency = computed(() => {
  return eventsStore.currency
})

const { sendData } = useFrameBridge()
const isMounted = ref(false)
const isExternalCart = ref(false)
const isWaitExternalCart = ref(false)
let externalCartWaitTimeout: number | null = null

onServerPrefetch(async () => {
  isTicketsLoading.value = true
  await eventsStore.fetchEvent(eventSlug, false)
  await ordersStore.fetchTicketListDetails(eventsStore.event?.id)
  isTicketsLoading.value = false
})

onMounted(async () => {
  if (!useNuxtApp().isHydrating) {
    isTicketsLoading.value = true
    await eventsStore.fetchEvent(eventSlug, false)
    await ordersStore.fetchTicketListDetails(eventsStore.event?.id)
    isTicketsLoading.value = false
  }

  emit('loaded', ticketsBlock, 'tickets')

  const savedTickets = getSessionStorageValue('tickets')

  if (savedTickets) {
    if (savedTickets.eventId === eventsStore.event?.id) {
      ordersStore.selectedPackages = {}
      selectedTickets.value = savedTickets.list
    } else {
      removeSessionStorageValue('tickets')
    }
  }

  if (props.isInIframe) {
    initExternalCart()
  }

  isMounted.value = true
})

onUnmounted(() => {
  isMounted.value = false
  window.removeEventListener('message', onExternalCartInitialized)
})

const autoColsOnDesktop = computed(() =>
  props.formData.autoColsOnDesktop !== undefined
    ? props.formData.autoColsOnDesktop
    : true
)

const colsNumberOnLargeDesktop = computed(() => {
  if (autoColsOnDesktop.value) {
    const itemCount = availableTicketsCategories.value?.length || 0

    if (itemCount < 4) {
      return 3
    }

    const lastRowItemCount = itemCount % 4

    return [0, 3].includes(lastRowItemCount) ? 4 : 3
  }

  return props.formData.colsNumberOnDesktop || 4
})
const colsNumberOnSmallDesktop = computed(() => {
  if (autoColsOnDesktop.value) {
    const itemCount = availableTicketsCategories.value?.length || 0

    if (itemCount < 3) {
      return 2
    }

    const lastRowItemsCount = itemCount % 3

    return lastRowItemsCount !== 1 ? 3 : 2
  }

  return Math.min(props.formData.colsNumberOnDesktop || 3, 3)
})

const initExternalCart = () => {
  isWaitExternalCart.value = true

  externalCartWaitTimeout && window.clearTimeout(externalCartWaitTimeout)

  externalCartWaitTimeout = window.setTimeout(() => {
    isWaitExternalCart.value = false
  }, 100)

  window.addEventListener('message', onExternalCartInitialized)
  sendData({
    message_type: 'init_cart',
    count: ticketsCounter.value,
    link: ordersStore.cartLink,
  })
}

const onExternalCartInitialized = (event: MessageEvent) => {
  if (event.data.message_type !== 'cart_initialized') {
    return
  }

  externalCartWaitTimeout && window.clearTimeout(externalCartWaitTimeout)

  isWaitExternalCart.value = false
  isExternalCart.value = true
}

const onUpdate = (ticketId: number, quantity: number) => {
  ordersStore.selectedPackages = {}
  removeSessionStorageValue('packages')

  selectedTickets.value[ticketId] = quantity

  if (isExternalCart.value) {
    sendData({
      message_type: 'update_cart',
      count: ticketsCounter.value,
      link: ordersStore.cartLink,
    })
  } else {
    isCartExpanded.value = true
    setTimeout(() => {
      isCartExpanded.value = false
    }, 150)
  }

  if (ticketsCounter.value) {
    setSessionStorageValue('tickets', {
      eventId: eventsStore.event?.id,
      list: selectedTickets.value,
    })
  } else {
    removeSessionStorageValue('tickets')
  }

  lintrk('track', { conversion_id: 17206282 })
  fbq('track', 'AddToCart')
  gTagPush({ event: 'AddToCart' })
  sendECommerceAddEvent(ticketId, quantity)
}

const onClickOnCart = () => {
  lintrk('track', { conversion_id: 17206306 })
  fbq('track', 'ViewContent')
  gTagPush({ event: 'ViewCartContent' })
}

function onVisibleChange(isVisible: boolean): void {
  if (isVisible) {
    sendECommerceImpressionsEvent()
  }
}

let isECommerceImpressionsEventSent = false

function sendECommerceImpressionsEvent() {
  if (!props.formData?.categories || isECommerceImpressionsEventSent) {
    return
  }

  isECommerceImpressionsEventSent = true

  const tickets: TicketDetails[] = []

  props.formData.categories.forEach((category: Category) => {
    category.tickets?.forEach((ticketParams) => {
      const ticketId = ticketParams.value

      const ticket = ordersStore.ticketsDetailsMap[ticketId]
      ticket && tickets.push(ticket)
    })
  })

  sendYandexECommerceEvent({
    currencyCode: currency.value,
    [YandexECommerceEventType.Impressions]: tickets.map((ticket, index) =>
      getYandexECommerceProductFromTicket(
        ticket,
        currency.value,
        eventsStore.event,
        1,
        '',
        0,
        index
      )
    ),
  })

  sendViewItemListGoogleECommerceEvent(
    {
      currency: currency.value,
    },
    tickets.map((ticket, index) =>
      getGoogleECommerceItemFromTicket(
        ticket,
        currency.value,
        eventsStore.event,
        1,
        '',
        0,
        index
      )
    )
  )
}

function sendECommerceAddEvent(ticketId: number, quantity: number): void {
  const ticket = ordersStore.ticketsDetailsMap?.[ticketId]

  if (!ticket) {
    return
  }

  sendYandexECommerceEvent({
    currencyCode: currency.value,
    [YandexECommerceEventType.Add]: {
      products: [
        getYandexECommerceProductFromTicket(
          ticket,
          currency.value,
          eventsStore.event,
          quantity
        ),
      ],
    },
  })

  sendAddToCartGoogleECommerceEvent(currency.value, [
    getGoogleECommerceItemFromTicket(
      ticket,
      currency.value,
      eventsStore.event,
      quantity
    ),
  ])
}

const onRegisterFreeTicket = async (ticketId: TicketDetails['id']) => {
  if (!authStore.auth) {
    authStore.openLoginModal()
    return
  }

  const eventId = eventsStore.event?.id

  if (
    !ticketId ||
    freeTicketRegistrationId.value ||
    !eventId ||
    eventsStore.event?.is_has_ticket
  ) {
    return
  }

  try {
    freeTicketRegistrationId.value = ticketId

    const result = await ordersStore.registerFreeTicket(eventId, ticketId)

    await navigateTo(
      useLocaleRoute()(`/users/me/orders/${result.order_id}?status=success`)
    )

    eventsStore.event?.id === eventId && eventsStore.resetEvent()
  } catch (error: any) {
    pushNotification({
      title: t('notifications.tickets.free_ticket_registration_error'),
      theme: 'destructive',
    })
    useLogError(error)
  } finally {
    freeTicketRegistrationId.value = null
  }
}
</script>

<style lang="scss" scoped>
.tickets-container {
  container-type: inline-size;
}

.tickets-inner {
  --tickets-gap: 1rem;
  gap: var(--tickets-gap);

  &.oneItem {
    justify-content: var(--tickets-items-align-desktop);
  }

  @container (min-width: 890px) {
    justify-content: var(--tickets-items-align-desktop);
  }

  &.in-iframe {
    @media (min-width: 890px) {
      /* fallback for old browsers */
      justify-content: var(--tickets-items-align-desktop);
    }
  }

  @screen lg {
    /* fallback for old browsers */
    justify-content: var(--tickets-items-align-desktop);
  }
}

.ticket-card-width {
  --ticket-card-width: calc(
    (100% - (var(--tickets-columns) - 1) * var(--tickets-gap)) /
      var(--tickets-columns)
  );

  /*
   Just in case, we apply this trick so that due to floating-point
   calculations at different zoom levels,
   the last item in the row never unexpectedly wraps to a new line
  */
  flex: 1 0 auto;
  width: calc(var(--ticket-card-width) - 1px);
  max-width: var(--ticket-card-width);

  --tickets-columns: 1;

  @container (min-width: 620px) {
    --tickets-columns: 2;
  }

  @screen md {
    /* fallback for old browsers */
    --tickets-columns: 2;
  }

  @container (min-width: 890px) {
    --tickets-columns: var(--tickets-columns-small-desktop, 3);
  }

  &.in-iframe {
    @media (min-width: 890px) {
      /* fallback for old browsers */
      --tickets-columns: var(--tickets-columns-small-desktop, 3);
    }
  }

  @screen lg {
    /* fallback for old browsers */
    --tickets-columns: var(--tickets-columns-small-desktop, 3);
  }

  @container (min-width: 1100px) {
    --tickets-columns: var(--tickets-columns-large-desktop, 4);
  }

  &.in-iframe {
    @media (min-width: 1100px) {
      /* fallback for old browsers */
      --tickets-columns: var(--tickets-columns-large-desktop, 4);
    }
  }

  @screen xl {
    /* fallback for old browsers */
    --tickets-columns: var(--tickets-columns-large-desktop, 4);
  }
}
</style>
